import { IrouterNav, TnavRoute } from '../types/general';
import { Eventbus } from './eventbus';

/**
 * Creates a navigation utility that combines router navigation with event bus communication.
 * This is useful for triggering side effects (like state updates) when navigating.
 *
 * @template R - A type that extends IrouterNav, representing the router interface
 * @param {Eventbus} eventbus - The event bus instance for publishing navigation events
 * @param {R} router - The router instance that implements IrouterNav interface
 * @returns {Object} An object containing the navigateByBus function
 *
 * @example
 * const { navigateByBus } = navByBus(eventbus, router);
 * navigateByBus('/dashboard', 'NAVIGATE_TO_DASHBOARD', { userId: '123' });
 */
export const navByBus = <R extends IrouterNav>(eventbus: Eventbus, router: R) => {
  /**
   * Navigates to a route and publishes an event with the provided data.
   * @template T - Type of the data being passed with the event
   * @param {TnavRoute} route - The route to navigate to
   * @param {string} type - The event type to publish
   * @param {T} data - The data to include in the event
   * @returns {void}
   *
   * @example
   * navigateByBus('/user/123', 'USER_NAVIGATED', { userId: '123', from: 'dashboard' });
   */
  const navigateByBus = <T>(route: TnavRoute, type: string, data: T): void => {
    // Publish the navigation event
    eventbus.generalEvent$.next({
      type,
      data
    });

    // Perform the navigation
    router.navigate(route);
  };

  return {
    navigateByBus
  };
};